import {SortingState} from '@tanstack/table-core';
import {MRT_ColumnSizingState} from 'material-react-table';
import React from 'react';
import Table from '@/components/table';
import useCreatorsTabColumns from '../../hooks/use-creators-tab-columns';
import type {
  CampaignCreatorRow,
  PaidGigCreatorStatus,
  CampaignDeliverables,
  PageSelectionState,
} from '../../types/creatorTabTypes';

interface CreatorsTableProps {
  data: CampaignCreatorRow[];
  activeStatus: PaidGigCreatorStatus;
  hasFulfillment?: boolean;
  deliverablesType?: CampaignDeliverables | null;
  campaignNetwork?: string | null;
  sorting: SortingState;
  onSorting: (sorting: SortingState) => void;
  onActionComplete: () => void;
  onRowClick?: (row: CampaignCreatorRow) => void;
  visibleColumns?: Record<string, boolean>;
  selectedCreatorIds?: Set<string>;
  pageSelectionState?: PageSelectionState;
  onSelectionChange?: (creatorId: string, isSelected: boolean) => void;
  onSelectAllPage?: (isSelected: boolean) => void;
  rowSelection?: Record<string, boolean>;
  onEndReached?: () => void;
  isLoading?: boolean;
  /** IDs of creators currently shown in the preview side panel. */
  highlightedRowIds?: Set<string>;
  /** Soft highlight for the most recently viewed creator after the panel closes. */
  lastViewedRowIds?: Set<string>;
  /** Returns true when the row has a non-empty local message draft. */
  hasMessageDraft?: (row: CampaignCreatorRow) => boolean;
  /**
   * Optional custom empty state shown when `data` is empty. When omitted, the
   * underlying Material React Table renders its default "No records to
   * display" message.
   */
  emptyStateComponent?: React.ReactNode;
  columnSizing?: MRT_ColumnSizingState;
  onColumnSizingChange?: (sizing: MRT_ColumnSizingState) => void;
}

const EMPTY_VISIBLE_COLUMNS: Record<string, boolean> = {};
const EMPTY_ROW_SELECTION: Record<string, boolean> = {};

function CreatorsTable({
  data,
  activeStatus,
  hasFulfillment = false,
  deliverablesType = null,
  campaignNetwork = null,
  sorting,
  onSorting,
  onActionComplete,
  onRowClick,
  visibleColumns = EMPTY_VISIBLE_COLUMNS,
  selectedCreatorIds,
  pageSelectionState,
  onSelectionChange,
  onSelectAllPage,
  rowSelection,
  onEndReached,
  isLoading,
  highlightedRowIds,
  lastViewedRowIds,
  hasMessageDraft,
  emptyStateComponent,
  columnSizing,
  onColumnSizingChange,
}: CreatorsTableProps) {
  const columns = useCreatorsTabColumns({
    activeStatus,
    onActionComplete,
    hasFulfillment,
    deliverablesType,
    campaignNetwork,
    selectedCreatorIds,
    pageSelectionState,
    onSelectionChange,
    onSelectAllPage,
    hasMessageDraft,
  });

  return (
    <Table
      data={data}
      columns={columns}
      sorting={sorting}
      onSorting={onSorting}
      isLoadingInitial={isLoading && data.length === 0}
      showProgressBars={false}
      visibleColumns={visibleColumns}
      rowSelection={rowSelection ?? EMPTY_ROW_SELECTION}
      enableSorting
      enableVirtualization
      enableColumnResizing
      columnSizing={columnSizing}
      onColumnSizingChange={onColumnSizingChange}
      manualSorting={false}
      onRowClick={onRowClick}
      onEndReached={onEndReached}
      ignorePortalRowClicks
      tabIndex={0}
      highlightedRowIds={highlightedRowIds}
      lastViewedRowIds={lastViewedRowIds}
      emptyStateComponent={emptyStateComponent}
    />
  );
}

export default CreatorsTable;
